import React, { useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';
import { toast } from 'react-toastify';
import { Link, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { UncontrolledTooltip } from 'reactstrap';
import { FlagImage } from 'react-international-phone';
import SocialAccountHandle from 'Components/social-account/SocialAccountHandle';
import { useCountries } from 'hooks/useCountries';
import { onboardCreator } from 'services/api/influencer';
import { toggleSocialAccountOnboarding } from 'slices/ui/reducer';
import { AppDispatch } from 'store';
import { CampaignInvite } from 'types/campaign/invites';
import { copyToClipboard, formatNumber, getErrorMessage } from 'utils/helpers';

interface InvitesTableProps {
    invites: CampaignInvite[];
    hasMore: boolean;
    loading: boolean;
    initialLoading: boolean;
    error: string | null;
    onNextPage: () => void;
    showCampaignColumn?: boolean;
    actionMode?: 'onboard' | 'dm';
}

const yesNo = (value: boolean) => (value ? 'Y' : 'N');

const PlatformIcon = ({ platform }: { platform: string }) => {
    if (platform === 'ig') {
        return <i className="ri-instagram-line text-danger me-1" aria-hidden />;
    }
    if (platform === 'yt') {
        return <i className="ri-youtube-fill text-danger me-1" aria-hidden />;
    }
    return null;
};

const InvitesTable: React.FC<InvitesTableProps> = ({
    invites,
    hasMore,
    loading,
    initialLoading,
    error,
    onNextPage,
    showCampaignColumn = false,
    actionMode = 'onboard',
}) => {
    const [onboardingId, setOnboardingId] = useState<string | null>(null);
    const [onboardedIds, setOnboardedIds] = useState<Set<string>>(new Set());
    const { countries } = useCountries();
    const dispatch = useDispatch<AppDispatch>();
    const { campaignId } = useParams();
    const columnCount = showCampaignColumn ? 12 : 11;

    const handleOnboard = async (invite: CampaignInvite) => {
        const socialAccountId = invite.social_account?.uuid;
        const handle = invite.social_account?.handle;
        if (!socialAccountId || onboardingId || onboardedIds.has(socialAccountId)) return;

        setOnboardingId(socialAccountId);
        try {
            const message = await onboardCreator(socialAccountId, 'campaign_invitation', campaignId);
            setOnboardedIds((prev) => new Set(prev).add(socialAccountId));

            if (message) {
                await copyToClipboard(message, 'Message copied to clipboard');
            }

            dispatch(toggleSocialAccountOnboarding({
                message: message || '',
                handle: handle || '',
                profileUrl: handle ? `https://ig.me/m/${handle}` : '#',
            }));
        } catch (err) {
            toast.error(getErrorMessage(err, 'Failed to onboard creator'));
        } finally {
            setOnboardingId(null);
        }
    };

    const handleDm = (invite: CampaignInvite) => {
        const handle = invite.social_account?.handle;
        if (!handle) return;
        window.open(`https://ig.me/m/${handle}`, '_blank', 'noopener,noreferrer');
    };

    return (
        <>
            <InfiniteScroll
                dataLength={invites.length}
                next={onNextPage}
                hasMore={hasMore}
                loader={<></>}
                className="w-100"
                style={{ overflow: 'visible' }}
            >
                <table>
                    <thead>
                        <tr>
                            <th>Influencer</th>
                            {showCampaignColumn && <th>Campaign</th>}
                            <th>Handle</th>
                            <th>Followers</th>
                            <th>ER</th>
                            <th>Mode</th>
                            <th>A</th>
                            <th>C</th>
                            <th>Notified</th>
                            <th>Delivery</th>
                            <th>By</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {!initialLoading &&
                            invites.map((invite, i) => {
                                const handle = invite.social_account?.handle;
                                const platform = invite.social_account?.platform;
                                const influencerName =
                                    invite.influencer?.name || invite.social_account?.name || '';
                                const socialAccountId = invite.social_account?.uuid;
                                const showOnboard =
                                    actionMode === 'onboard' &&
                                    !invite.influencer &&
                                    !!socialAccountId &&
                                    !onboardedIds.has(socialAccountId);
                                const showDm =
                                    actionMode === 'dm' && platform === 'ig' && !!handle;
                                const isOnboarding = onboardingId === socialAccountId;
                                const countryCode = invite.campaign?.country;
                                const countryOption = countries.find(
                                    (item) => item.value === (countryCode || '').toUpperCase()
                                );
                                const countryFlagId = `invite-country-flag-${socialAccountId || i}`;

                                return (
                                    <tr
                                        className="border-bottom-1 px-3"
                                        key={`${socialAccountId}-${invite.created}-${i}`}
                                    >
                                        <td>
                                            <div className="py-3">
                                                <div className="d-flex justify-content-start align-items-center gap-2 border-right-dashed td-content">
                                                    {
                                                        countryCode &&
                                                        <>
                                                            <span id={countryFlagId} className="d-inline-flex">
                                                                <FlagImage
                                                                    iso2={countryOption?.iso2 || countryCode.toLowerCase()}
                                                                    size="14px"
                                                                />
                                                            </span>
                                                            <UncontrolledTooltip
                                                                placement="top"
                                                                target={countryFlagId}
                                                                fade={false}
                                                            >
                                                                {countryOption?.label || countryCode.toUpperCase()}
                                                            </UncontrolledTooltip>
                                                        </>
                                                    }
                                                    <span className="influencer-name" title={influencerName || undefined}>
                                                        {influencerName || '-'}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        {showCampaignColumn && (
                                            <td>
                                                <div className="py-3">
                                                    <div className="d-flex justify-content-start align-items-center gap-1 border-right-dashed td-content">
                                                        {invite.campaign?.uuid ? (
                                                            <Link
                                                                to={`/campaign/${invite.campaign.uuid}/applications`}
                                                                className="text-primary invite-campaign-name"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title={invite.campaign.name || undefined}
                                                            >
                                                                {invite.campaign.name || '-'}
                                                            </Link>
                                                        ) : (
                                                            '-'
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        )}
                                        <td>
                                            <div className="py-3">
                                                <div className="d-flex justify-content-start align-items-center gap-1 border-right-dashed td-content">
                                                    {handle ? (
                                                        <span
                                                            className="text-primary d-inline-flex align-items-center invite-handle"
                                                            title={handle}
                                                        >
                                                            <PlatformIcon platform={platform} />
                                                            <SocialAccountHandle
                                                                socialAccountId={socialAccountId}
                                                                influencerId={invite.influencer?.uuid}
                                                                handle={handle}
                                                                platform={platform}
                                                                showAt={false}
                                                                className="text-primary"
                                                            />
                                                        </span>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="py-3">
                                                <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                    <span>
                                                        {formatNumber(
                                                            invite.social_account?.followers_count ?? 0
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="py-3">
                                                <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                    <span>
                                                        {invite.social_account?.engagement_score ?? '-'}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="py-3">
                                                <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                    <span>{invite.mode || '-'}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="py-3">
                                                <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                    <span>{yesNo(invite.applied)}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="py-3">
                                                <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                    <span>{yesNo(invite.collaborated)}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="py-3">
                                                <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                    <span>{invite.whatsapp_notified || '-'}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="py-3">
                                                <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                    <span>{invite.whatsapp_delivery_status || '-'}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="py-3">
                                                <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                    <span>{invite.user?.name || '-'}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="py-3">
                                                <div className="d-flex justify-content-center align-items-center gap-1 td-content invite-action-cell">
                                                    {showOnboard && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-soft-primary"
                                                            title="Onboard creator"
                                                            disabled={isOnboarding}
                                                            onClick={() => handleOnboard(invite)}
                                                        >
                                                            {isOnboarding ? (
                                                                <i className="bx bx-loader-alt bx-spin" />
                                                            ) : (
                                                                <i className="ri-user-add-line" />
                                                            )}
                                                        </button>
                                                    )}
                                                    {showDm && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-soft-primary"
                                                            title="DM Creator"
                                                            onClick={() => handleDm(invite)}
                                                        >
                                                            <i className="ri-send-plane-2-line" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        {(loading || initialLoading) &&
                            Array.from({ length: 10 }).map((_, i) => (
                                <tr key={`skeleton-${i}`}>
                                    {Array.from({ length: columnCount }).map((__, j) => (
                                        <td key={j} height={30}>
                                            <Skeleton />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                    </tbody>
                </table>
                {!(loading || initialLoading) && error && (
                    <div className="py-3 d-flex justify-content-center align-items-center">
                        <button
                            type="button"
                            className="theme-btn theme-btn-sm btn-primary-outlined"
                            onClick={onNextPage}
                        >
                            Load More <i className="ms-2 fs-5 bx bx-refresh" />
                        </button>
                    </div>
                )}
                {!(loading || initialLoading) && invites.length === 0 && (
                    <div className="d-flex w-100 justify-content-center align-items-center my-5">
                        <div className="length-zero my-5">
                            <h3>Currently there are no invites in your list</h3>
                        </div>
                    </div>
                )}
            </InfiniteScroll>
        </>
    );
};

export default InvitesTable;
